import { ExhibitDetails } from '@telescope/veh-shared-components/dist/lib/models';
import { css } from 'aphrodite/no-important';
import 'core-js/es/string/replace-all';
import useAchievements, { CATEGORIES } from 'hooks/useAchievements';
import { useEventListener } from 'hooks/useEventListener';
import { UserData } from 'models';
import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import LiveChatService from 'services/live-chat.service';
import { Channel as ChannelType, StreamChat } from 'stream-chat';
import { Channel, Chat, MessageInput, MessageList, Window } from 'stream-chat-react';
import 'stream-chat-react/dist/css/index.css';
import { CloseIcon } from 'util/icons';
import vehApi from 'util/veh-api';
import { ChannelHeader } from './ChannelHeader/ChannelHeader';
import './LiveChat.css';
import { LiveChatLoading } from './LiveChatLoading/LiveChatLoading';
import { MessageCommerce } from './Message/Message';
import { AttendeeMessageInput } from './MessageInput/AttendeeMessageInput';
import { style } from './style';

const TABLET_WIDTH = 768; // TODO: get this info from partnerDetail
interface Props {
  showChat: boolean;
  userData: UserData | null;
  exhibitData: ExhibitDetails;
  onRequestClose: () => void;
}
export const LiveChat: FC<Props> = ({ showChat, userData, onRequestClose, exhibitData }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [client, setClient] = useState<StreamChat | null>(null);
  const [channel, setChannel] = useState<ChannelType>();
  const [isMobile, setIsMobile] = useState(window.innerWidth < TABLET_WIDTH);
  const [vendorUsers, setVendorUsers] = useState({ isInit: false, vendorUserIds: [] as string[] });
  const { submitAchievement } = useAchievements();
  const chatChannelGroup = exhibitData.live_chat.chat_channel_group;
  const styles = style({ isActive: showChat });
  let scrollPosition = useRef<any>();

  // If the chat is opened in mobile (fullscreen), then we need to prevent background scrolling,
  // but we should not lock scroll on tablet or desktop
  const resizeHandler = useCallback(() => {
    window.innerWidth < TABLET_WIDTH ? setIsMobile(true) : setIsMobile(false);
  }, []);
  useEventListener('resize', resizeHandler);

  useEffect(() => {
    if (showChat) {
      submitAchievement(CATEGORIES.CHAT, 'CHAT1');
    }
  }, [showChat, submitAchievement]);

  useEffect(() => {
    if (vendorUsers.isInit || !chatChannelGroup) return;
    const getVendorUserIds = async () => {
      try {
        const ids = await vehApi.getVendorUserIds(chatChannelGroup);
        setVendorUsers({ isInit: true, vendorUserIds: ids });
      } catch (e) {
        console.error(`Unable to fetch vendor user ids with chatChannelGroup: ${chatChannelGroup}`);
      }
    };
    getVendorUserIds();
  }, [chatChannelGroup, vendorUsers.isInit, showChat]);

  const handleClose = () => {
    // Keep current scroll position upon close
    const $body = document.body;
    $body.style.removeProperty('overflow');
    $body.style.removeProperty('position');
    $body.style.removeProperty('top');
    $body.style.removeProperty('width');
    window.scrollTo(0, window.pageYOffset || 0);
    onRequestClose();
  };

  useLayoutEffect(() => {
    const $body = document.body;
    if (showChat && isMobile) {
      // Save the user's current scroll position
      scrollPosition.current = window.pageYOffset;
      $body.style.overflow = 'hidden';
      $body.style.position = 'fixed';
      $body.style.width = '100%';
      $body.style.top = `-${scrollPosition.current}px`;

      return () => {
        const $body = document.body;
        $body.style.removeProperty('overflow');
        $body.style.removeProperty('position');
        $body.style.removeProperty('top');
        $body.style.removeProperty('width');
        window.scrollTo(0, scrollPosition.current || 0);
      };
    }
  }, [isMobile, showChat]);

  // connect user
  useEffect(() => {
    if (!showChat || !userData || !vendorUsers.isInit) return;
    const connectToChat = async () => {
      setIsLoading(true);
      const chatClient = LiveChatService.chatClient;
      try {
        const chatChannel = await LiveChatService.connect(userData, chatChannelGroup, vendorUsers.vendorUserIds);
        setClient(chatClient);
        setChannel(chatChannel);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
      }
    };

    connectToChat();

    // close the connection on unmount
    return () => {
      LiveChatService.disconnect();
      setClient(null);
    };
  }, [showChat, userData, chatChannelGroup, vendorUsers.isInit, vendorUsers.vendorUserIds]);

  const countOnlineVendors = useCallback(
    async function () {
      try {
        const queryResult = await channel?.queryMembers({});
        const onlineVendors = queryResult?.members.filter(
          (member) => member?.user_id !== userData?.code && member.user?.online
        );
        return onlineVendors ? onlineVendors.length : -1;
      } catch (e) {
        console.error(e);
        return -1;
      }
    },
    [channel, userData]
  );

  return (
    <div className={css(styles.chatContainer, showChat ? styles.showChat : styles.hideChat)}>
      <div className={css(styles.header)}>
        <span>LIVE CHAT</span>
        <button className={css(styles.closeButton)} onClick={handleClose}>
          <CloseIcon></CloseIcon>
        </button>
      </div>
      {!isLoading && client && channel && showChat ? (
        <Chat client={client} theme={LiveChatService.chatChannelType}>
          <div className={`customer-wrapper ${showChat ? 'wrapper--open' : ''}`}>
            <Channel channel={channel}>
              <Window>
                <ChannelHeader
                  name={exhibitData.live_chat.chat_display_name}
                  email={exhibitData.live_chat.chat_offline_email}
                  channelImage={exhibitData.live_chat.chat_avatar}
                  attendeeId={userData?.code}
                  onUserPresenceChange={() => countOnlineVendors()}
                />
                <MessageList Message={MessageCommerce} />
                <MessageInput grow maxRows={3} focus Input={AttendeeMessageInput} noFiles={true} />
              </Window>
            </Channel>
          </div>
        </Chat>
      ) : (
        <LiveChatLoading />
      )}
    </div>
  );
};
